import { QueryClient } from "@tanstack/vue-query";
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

function trimFeedInfinitePagesForPersistence(persistedClient: unknown): unknown {
    if (
        !persistedClient ||
        typeof persistedClient !== "object" ||
        !("clientState" in persistedClient)
    ) {
        return persistedClient;
    }

    const pc = persistedClient as {
        clientState?: {
            queries?: Array<{
                queryKey?: unknown[];
                state?: { data?: { pages?: unknown[]; pageParams?: unknown[] } };
            }>;
        };
    };

    const queries = pc.clientState?.queries;
    if (!Array.isArray(queries)) return persistedClient;

    for (const query of queries) {
        const key0 = Array.isArray(query.queryKey) ? query.queryKey[0] : undefined;
        const data = query.state?.data;

        if (
            key0 === "feeds" &&
            data &&
            Array.isArray(data.pages) &&
            data.pages.length > 1
        ) {
            data.pages = data.pages.slice(0, 1);

            if (Array.isArray(data.pageParams)) {
                data.pageParams = data.pageParams.slice(0, 1);
            }
        }
    }

    return persistedClient;
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30 * 1000, // 30 seconds — feed data changes moderately; balance freshness vs network
            gcTime: 5 * 60 * 1000, // 5 minutes — free memory sooner for feed-heavy pages
            retry: 1,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: true,
        },
        mutations: {
            retry: false,
        },
    },
});

// --- Persistence setup (runs before app mount) ---
// Restore data from localStorage to survive browser refreshes.
// Feed queries are persisted to reduce initial load latency.

try {
    const persister = createAsyncStoragePersister({
        storage: window.localStorage,
        key: "tanstack-query-cache",
        throttleTime: 1000, // Debounce writes -- without this every cache change triggers a full serialize + write
        serialize: (client) => {
            // structuredClone is REQUIRED here: TanStack's dehydrate() passes query.state by
            // reference (not a deep copy). Without cloning, trimFeedInfinitePagesForPersistence
            // would mutate TanStack's internal state.data.pages directly, collapsing multi-page
            // infinite queries to 1 page in memory every time the persist throttle fires.
            const cloned = structuredClone(client as object);
            const trimmed = trimFeedInfinitePagesForPersistence(cloned) as {
                clientState?: unknown;
            };

            return JSON.stringify(trimmed);
        },
        deserialize: (cachedString) => JSON.parse(cachedString),
    });

    persistQueryClient({
        queryClient,
        persister,
        maxAge: 5 * 60 * 1000, // 5 minutes — matches global gcTime
        buster: `${import.meta.env.VITE_APP_VERSION ?? ""}-feed-persistence-v2`, // v2: persistence narrowed to feed queries only — discard older blobs that cached non-feed queries
        dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
                const key0 = Array.isArray(query.queryKey)
                    ? query.queryKey[0]
                    : undefined;
                return query.state.status === "success" && key0 === "feeds";
            },
        },
    });
} catch (e) {
    console.warn(
        "[QueryClient] Persistence setup failed, falling back to in-memory cache:",
        e,
    );
}
